import {
  Currency,
  formatMoney,
  getMoneySymbolByCurrency,
  MainResidenceDetails,
  MainResidenceDetailsSchema,
  ResidenceOwnership,
} from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { FormGridOptions } from '@/components/shared/components/FormGridContainer/types/formGridOptions';
import { AutocompleteAddress } from '@/components/shared/components/NewContextForms/components/AutocompleteAddress/AutocompleteAddress';
import { DateFormField } from '@/components/shared/components/NewContextForms/DateFormField';
import { NumberFormField } from '@/components/shared/components/NewContextForms/NumberFormField';
import { SelectFormField } from '@/components/shared/components/NewContextForms/SelectFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { getBlankDefaultValuesFromZodSchema } from '@/utils/getBlankDefaultValuesFromZodSchema';
import { amountToValueMoneyMapper } from '@/utils/mappers/amountToValueMoneyMapper';

import { mortgageTypeArray, ownershipTypeArray, propertyTypeArray } from '../config';

export interface MainResidenceFormProps extends FormGridOptions {
  currency?: Currency;
  mainResidenceDetails?: MainResidenceDetails;
  showAddressHint?: boolean;
}

export function MainResidenceForm({
  currency,
  mainResidenceDetails = getBlankDefaultValuesFromZodSchema(MainResidenceDetailsSchema, currency),
  fullWidth = false,
  fieldVariant,
  direction,
}: MainResidenceFormProps) {
  const watch = useFormContext()?.watch;
  const setValue = useFormContext()?.setValue;
  const resetField = useFormContext()?.resetField;

  const { getTextIdTranslation } = useTranslation();
  const ownershipType = watch ? watch('ownershipType') : mainResidenceDetails.ownershipDetails.ownershipType;
  const isOwnership = ownershipType === ResidenceOwnership.OWNERSHIP;

  useEffect(() => {
    if (isOwnership && resetField) {
      resetField('propertyType');
      resetField('mortgage');
    } else if (setValue) {
      setValue('propertyType', null);
      setValue('mortgage', null);
    }
  }, [ownershipType]);

  const showDetailedAddressForm = fieldVariant === FieldVariant.form;

  return (
    <>
      <FormGridContainer fieldVariant={fieldVariant} fullWidth={fullWidth} direction={direction}>
        <SelectFormField
          name={'ownershipType'}
          label={'OWNERSHIP_TYPE'}
          value={mainResidenceDetails.ownershipDetails.ownershipType}
          selectOptions={ownershipTypeArray}
        />
        {ownershipType === ResidenceOwnership.OWNERSHIP && (
          <SelectFormField
            name={'propertyType'}
            label={'CONTRACT_TYPE'}
            value={mainResidenceDetails.ownershipDetails.propertyType}
            selectOptions={propertyTypeArray}
            fieldVariant={fieldVariant}
          />
        )}
        <NumberFormField
          name={'homeExpenses'}
          label={'HOME_EXPENSES'}
          valueUnit={mainResidenceDetails.homeExpenses.currency ?? getMoneySymbolByCurrency(currency ?? Currency.EUR)}
          value={
            mainResidenceDetails.homeExpenses &&
            formatMoney(amountToValueMoneyMapper(mainResidenceDetails.homeExpenses))
          }
          fieldVariant={fieldVariant}
        />
        {ownershipType === ResidenceOwnership.OWNERSHIP && (
          <SelectFormField
            name={'mortgage'}
            label={'MORTGAGE'}
            value={mainResidenceDetails.ownershipDetails.mortgage ? 'YES' : 'NO'}
            selectOptions={mortgageTypeArray}
            fieldVariant={fieldVariant}
          />
        )}
        <DateFormField
          name={'entranceDate'}
          label={'ENTRANCE_DATE'}
          value={mainResidenceDetails.entranceDate}
          fieldVariant={fieldVariant}
          views={['year', 'month']}
          format={'MM/yyyy'}
          placeholder={getTextIdTranslation('DATE_FORMAT_MONTH_YEAR')}
          disableFuture
        />
        {!showDetailedAddressForm && (
          <TextFormField
            maxRows={10}
            name={'mainResidenceAddress.address'}
            label={'MAIN_RESIDENCE_ADDRESS'}
            value={mainResidenceDetails.address.formattedAddress}
            fieldVariant={FieldVariant.dataRow}
          />
        )}
        {!showDetailedAddressForm && (
          <TextFormField
            name={'mainResidenceAddress.floorNumber'}
            label={'FLOOR_NUMBER'}
            value={mainResidenceDetails.address.floorNumber}
            fieldVariant={fieldVariant}
            disableTranslation
            markAsOptional
          />
        )}
        {!showDetailedAddressForm && (
          <TextFormField
            name={'mainResidenceAddress.doorNumber'}
            label={'DOOR_NUMBER'}
            value={mainResidenceDetails.address.doorNumber}
            fieldVariant={fieldVariant}
            disableTranslation
            markAsOptional
          />
        )}
        {!showDetailedAddressForm && (
          <TextFormField
            name={'mainResidenceAddress.building'}
            label={'BUILDING_LABEL'}
            value={mainResidenceDetails.address.building}
            fieldVariant={fieldVariant}
            disableTranslation
            markAsOptional
          />
        )}
        {useFormContext() && (
          <AutocompleteAddress fieldVariant={fieldVariant} fieldNamesPrefix={'mainResidenceAddress'} />
        )}
      </FormGridContainer>
    </>
  );
}
